import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import moment from 'moment';
import {
  getSingleCarSale,
  deleteCarSale,
  updateCarSaleAdmin,
} from '../../actions/homeAction';

const card = {
  background: '#fff',
  borderRadius: 8,
  padding: '1.25rem 1.5rem',
  marginBottom: '1.5rem',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};
const h = { fontWeight: 600, color: '#1b2a4e', marginBottom: '1rem' };
const fmt = (d) => (d ? moment(d).format('DD/MM/YYYY , hh:mm A') : '—');

const CarSaleView = () => {
  const dispatch = useDispatch();
  const { carSaleId } = useParams();
  const [form, setForm] = useState({ price: '', description: '', kilometerDriven: '', status: '' });

  const refetch = () => dispatch(getSingleCarSale(carSaleId));

  useEffect(() => {
    if (carSaleId) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carSaleId]);

  const single = useSelector((state) => state.home.singleCarSale);
  // getSingleCarSale stores res.data.response, which is shaped { data: [ car ] }.
  const arr = Array.isArray(single)
    ? single
    : single?.data || single?.response?.data || (single ? [single] : []);
  const car = arr[0];

  useEffect(() => {
    if (car) {
      setForm({
        price: car.price ?? '',
        description: car.description ?? '',
        kilometerDriven: car.kilometerDriven ?? '',
        status: car.status ?? '',
      });
    }
  }, [car]);

  if (!car) {
    return (
      <div className="pcoded-inner-content">
        <div className="main-body"><div className="page-wrapper"><h4>Loading...</h4></div></div>
      </div>
    );
  }

  const images = (car.businessMedia || [])
    .map((m) => m?.media?.path)
    .filter(Boolean);

  const expiry =
    (Array.isArray(car.expiryDetails) ? car.expiryDetails[0] : car.expiryDetails) || {};
  const expiryRaw = expiry.expiryDate || expiry.Expiry || car.expiry;
  const daysLeft = expiryRaw
    ? Math.ceil((new Date(expiryRaw) - new Date()) / 86400000)
    : null;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = () => {
    dispatch(
      updateCarSaleAdmin(
        {
          carSaleId,
          price: Number(form.price),
          description: form.description,
          kilometerDriven: Number(form.kilometerDriven),
          status: form.status,
        },
        () => setTimeout(refetch, 400)
      )
    );
  };

  const onDelete = () => {
    if (window.confirm('Delete this car-for-sale post? This cannot be undone.')) {
      dispatch(
        deleteCarSale(carSaleId, () => {
          window.location.href = '#/ServiceProviders/CarForSale';
        })
      );
    }
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', padding: '0.4rem 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: 200, color: '#7b8aa5' }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );

  return (
    <div className="pcoded-inner-content">
      <div className="main-body">
        <div className="page-wrapper">
          <div className="row">
            <div className="col-sm-12">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ fontWeight: 600 }}>
                  Car for Sale — {car.brand?.name} {car.carModel}
                </h4>
                <div>
                  <button className="btn btn-sm btn-danger mr-2" onClick={onDelete}>Delete</button>
                  <Link to="/ServiceProviders/CarForSale" className="btn btn-sm btn-outline-dark">Back</Link>
                </div>
              </div>

              {/* Images */}
              {images.length > 0 && (
                <div style={card}>
                  <div style={h}>Photos</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {images.map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt="car"
                        style={{ width: 160, height: 110, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Subscription / expiry */}
              <div style={card}>
                <div style={h}>Listing Validity</div>
                {row('Expires on', fmt(expiryRaw))}
                {row(
                  'Days remaining',
                  daysLeft != null ? (daysLeft >= 0 ? `${daysLeft} day(s)` : `Expired ${Math.abs(daysLeft)} day(s) ago`) : '—'
                )}
                {row('Posted on', fmt(car.createdAt))}
              </div>

              {/* Vehicle details */}
              <div style={card}>
                <div style={h}>Vehicle Details</div>
                {row('Brand', car.brand?.name)}
                {row('Model / Series', car.carModel)}
                {row('Model Year', car.modelYear?.modelYear)}
                {row('Fuel Type', car.fuelType?.fuelType)}
                {row('Cylinders', car.cyclinder?.cyclinder || car.cyclinder?.name)}
                {row('Doors', car.doors?.door || car.doors?.name)}
                {row('Seats', car.seats?.seat || car.seats?.name)}
                {row('Interior Color', car.interiorColor?.color)}
                {row('Exterior Color', car.exteriorColor?.color)}
                {row('Kilometers Driven', car.kilometerDriven)}
              </div>

              {/* Contact */}
              <div style={card}>
                <div style={h}>Contact</div>
                {row('Phone', `${car.mobileNumberCountryCode || ''} ${car.mobileNumber || ''}`)}
                {row('WhatsApp', `${car.whatsAppNumberCountryCode || ''} ${car.whatsAppNumber || ''}`)}
              </div>

              {/* Editable fields */}
              <div style={card}>
                <div style={h}>Edit Listing</div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Price (KWD)</label>
                      <input type="number" className="form-control" name="price" value={form.price} onChange={onChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Kilometers Driven</label>
                      <input type="number" className="form-control" name="kilometerDriven" value={form.kilometerDriven} onChange={onChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Status</label>
                      <select className="form-control" name="status" value={form.status} onChange={onChange}>
                        <option value="active">Active</option>
                        <option value="in-active">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Description</label>
                      <textarea className="form-control" name="description" rows="3" value={form.description} onChange={onChange} />
                    </div>
                  </div>
                </div>
                <button className="btn btn-inverse" onClick={onSave}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarSaleView;
